// roleTokens.ts
import { makeRamp, STEP } from './ramp';
import { oklchHex, rgbToOklch, hexToRgb } from './oklch';
import { bestText } from './contrast';
import type { SeedConfig, TokenMap, ColoredRole } from './types';

const rgba = (hex: string, a: number): string => `rgba(${hexToRgb(hex).join(', ')}, ${a})`;

export function buildRoleTokens(config: SeedConfig): TokenMap {
  const { mode, roles } = config;
  const light = mode === 'light';
  const ramp = (seed: string) => makeRamp(seed, mode);
  const R = {
    brand: ramp(roles.brand),
    neutral: ramp(roles.neutral),
    danger: ramp(roles.danger),
    success: ramp(roles.success),
    warning: ramp(roles.warning),
    info: ramp(roles.info),
  };
  const n = R.neutral,
    b = R.brand;
  // Interactive accents (links, focus) — the optional accent seed, else the brand.
  const a = config.accent ? ramp(config.accent) : b;
  const t: TokenMap = {};

  // Surfaces + neutral-derived vocabulary
  t['surface-bg'] = light ? '#ffffff' : n[STEP.subtleHi]; // dark: card lighter than page
  t['surface-raised-bg'] = light ? '#ffffff' : n[STEP.componentHover]; // dark: overlay highest
  t['surface-sunken-bg'] = n[STEP.subtle]; // both modes: base recess
  t['bg-app'] = n[STEP.subtle]; // both modes: page base
  t['border'] = n[light ? STEP.componentHover : STEP.line];
  t['border-strong'] = n[light ? STEP.line : STEP.borderStrong];
  t['border-subtle'] = n[light ? STEP.subtleHi : STEP.componentHover]; // dark: 1 step above card
  t['fg'] = n[STEP.text];
  t['fg-subtle'] = n[light ? STEP.solidHover : STEP.solidActive];
  t['fg-muted'] = n[light ? STEP.solidLow : STEP.solid];
  t['fg-on-accent'] = '#ffffff';
  t['disabled-bg'] = n[light ? STEP.subtleHi : STEP.componentHover];
  t['disabled-fg'] = n[light ? STEP.border : STEP.borderStrong];
  t['input-bg'] = n[light ? STEP.subtleHi : STEP.componentHover];
  t['input-bg-hover'] = n[light ? STEP.componentHover : STEP.componentActive];
  t['overlay-bg'] = 'rgba(10, 30, 45, 0.35)';

  // Intent roles → bg/fg/border × states
  const intent = (name: keyof typeof R, r: string[]) => {
    const p = name as string;
    t[`${p}-bg`] = r[STEP.solid];
    t[`${p}-bg-hover`] = r[STEP.solidHover];
    t[`${p}-bg-active`] = r[STEP.solidActive];
    t[`${p}-bg-focus`] = r[STEP.solid];
    t[`${p}-bg-disabled`] = t['disabled-bg'];
    const fm = name === 'neutral' ? 'auto' : config.fgMode[name as ColoredRole];
    const fg = fm === 'white' ? '#ffffff' : fm === 'dark' ? '#0d1117' : bestText(r[STEP.solid]);
    t[`${p}-fg`] = fg;
    t[`${p}-fg-hover`] = fg;
    t[`${p}-fg-disabled`] = t['disabled-fg'];
    t[`${p}-subtle-bg`] = r[STEP.subtle];
    t[`${p}-subtle-bg-hover`] = r[STEP.subtleHi];
    t[`${p}-subtle-fg`] = r[STEP.text];
    t[`${p}-border`] = r[STEP.borderStrong];
    t[`${p}-border-hover`] = r[STEP.solidHover];
    t[`${p}-border-active`] = r[STEP.solidActive];
  };
  (['brand', 'neutral', 'danger', 'success', 'warning', 'info'] as const).forEach((k) =>
    intent(k, R[k])
  );

  // Focus + danger ring (soft translucent)
  t['focus-ring'] = a[STEP.solid];
  t['focus-ring-soft'] = rgba(a[STEP.solid], light ? 0.3 : 0.45);
  t['danger-ring-soft'] = rgba(R.danger[STEP.solid], light ? 0.28 : 0.42);

  // Nav (dedicated dark context, independent of mode). nav-accent stays on the
  // brand even with an accent seed: it is brand chrome, tuned (L 0.72) against
  // the brand-tinted nav-bg, not an interactive accent of the content.
  const bo = rgbToOklch(roles.brand);
  t['nav-bg'] = oklchHex(light ? 0.255 : 0.185, Math.max(bo.C * 0.75, 0.03), bo.H);
  t['nav-fg'] = '#f5f8fc';
  t['nav-fg-muted'] = 'rgba(245, 248, 252, 0.62)';
  t['nav-hover-bg'] = 'rgba(255, 255, 255, 0.09)';
  t['nav-active-bg'] = 'rgba(255, 255, 255, 0.15)';
  t['nav-accent'] = oklchHex(0.72, Math.max(bo.C * 0.9, 0.05), bo.H);

  // Table
  t['table-header-bg'] = n[STEP.componentHover];
  t['table-header-fg'] = t['fg-subtle'];
  t['table-zebra-bg'] = n[light ? STEP.subtleHi : STEP.subtle]; // dark: 1 step below row
  t['table-row-bg'] = t['surface-bg'];
  t['table-row-hover-bg'] = n[light ? STEP.componentHover : STEP.componentActive];
  t['table-row-active-bg'] = b[light ? STEP.subtleHi : STEP.componentHover];
  t['table-border'] = t['border'];
  t['table-filter-bg'] = t['input-bg'];

  // Text — same fixed ramp steps as the brand, so an accent link keeps the
  // contrast a brand link has (the ramp pins lightness, only hue/chroma move).
  t['link'] = a[STEP.solid];
  t['link-hover'] = a[STEP.solidHover];
  t['link-active'] = a[STEP.solidActive];

  return t;
}
